import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Send, Loader2, UserCheck, ClipboardList, LogOut, Repeat, Star } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { StatusBadge } from "../components/DashboardShell";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { eur, formatApiErrorDetail } from "../lib/api";

const CATS = [["preferiti", "I tuoi preferiti"], ["fusti", "Cocktail in fusto"], ["granite", "Preparati per granita"], ["liquori", "Liquori"], ["collezione", "Collezione"]];

export default function ListinoB2B() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [qty, setQty] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);
  const [last, setLast] = useState({});
  const [favs, setFavs] = useState(user?.favorites || []);
  const toggleFav = async (id) => {
    const on = !favs.includes(id);
    setFavs((f) => (on ? [...f, id] : f.filter((x) => x !== id)));
    try { const { data } = await api.put("/b2b/favorites", { product_id: id, on }); setFavs(data.favorites); }
    catch { toast.error("Impossibile aggiornare i preferiti"); }
  };
  const disc = Number(user?.discount_pct || 0);

  const loadOrders = () => api.get("/b2b/orders").then(({ data }) => setOrders(data)).catch(() => {});
  const loadLast = () => api.get("/b2b/last-prices").then(({ data }) => setLast(data)).catch(() => {});
  const repeatLast = () => {
    const last = orders[0];
    if (!last) return;
    const ids = new Set(products.map((p) => p.id));
    const next = {};
    let skipped = 0;
    (last.items || []).forEach((it) => { if (ids.has(it.product_id)) next[it.product_id] = it.quantity; else skipped += 1; });
    setQty(next);
    toast.success(skipped ? `Ultimo ordine caricato (${skipped} prodotto/i non più disponibile/i)` : "Ultimo ordine caricato: controlla e invia");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  useEffect(() => {
    document.title = "Listino operatori — Guatemalan Spirits";
    api.get("/products").then(({ data }) => setProducts(data.filter((p) => p.active !== false))).catch(() => {});
    loadOrders();
    loadLast();
  }, []);

  const price = (p) => Math.round((Number(p.agent_price ?? p.public_price) || 0) * (1 - disc / 100) * 100) / 100;
  const set = (id, n) => setQty((q) => ({ ...q, [id]: Math.max(0, Math.min(999, n)) }));
  const lines = useMemo(() => products.filter((p) => (qty[p.id] || 0) > 0).map((p) => ({ p, q: qty[p.id], line: price(p) * qty[p.id] })), [products, qty]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = lines.reduce((s, l) => s + l.line, 0);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/b2b/orders", { items: lines.map((l) => ({ product_id: l.p.id, quantity: l.q })), notes });
      toast.success("Ordine inviato al tuo agente"); setQty({}); setNotes(""); loadOrders(); loadLast();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="paper-grain min-h-screen">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-24" data-testid="listino-b2b">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="eyebrow text-[#C2593F] mb-2">Listino operatori</div>
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2A45]" data-testid="listino-title">{user?.business || user?.name}</h1>
            <p className="text-sm text-stone-600 mt-2 flex flex-wrap items-center gap-2">
              <UserCheck className="w-4 h-4 text-[#15803D]" />
              {user?.agent_name ? <>Il tuo agente: <b data-testid="listino-agent">{user.agent_name}</b></> : <>Agente in assegnazione: l'ordine arriva direttamente all'azienda</>}
              {disc > 0 && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#FEF9C3] text-[#854D0E]" data-testid="listino-discount">Sconto concordato -{disc}%</span>}
              <span className="text-xs text-stone-400">Prezzi IVA esclusa</span>
            </p>
          </div>
          <Button variant="outline" onClick={async () => { await logout(); navigate("/"); }} className="rounded-full" data-testid="listino-logout-btn"><LogOut className="w-4 h-4 mr-2" />Esci</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8 min-w-0">
            {CATS.map(([cat, label]) => {
              const list = products.filter((p) => (cat === "preferiti" ? favs.includes(p.id) : p.category === cat));
              if (!list.length) return null;
              return (
                <div key={cat} data-testid={`listino-cat-${cat}`}>
                  <h2 className="font-serif text-xl font-semibold text-[#0B2A45] mb-3 flex items-center gap-2">{cat === "preferiti" && <Star className="w-5 h-5 fill-[#EAB308] text-[#EAB308]" />}{label}</h2>
                  <div className="bg-white rounded-2xl border border-stone-200 divide-y divide-stone-100">
                    {list.map((p) => (
                      <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`listino-row-${cat}-${p.id}`}>
                        <button onClick={() => toggleFav(p.id)} className="shrink-0 p-1" data-testid={`listino-fav-${cat}-${p.id}`} aria-label="Preferito">
                          <Star className={`w-5 h-5 ${favs.includes(p.id) ? "fill-[#EAB308] text-[#EAB308]" : "text-stone-300"}`} />
                        </button>
                        {p.image_url && <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-cover hidden sm:block" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#0B2A45] truncate">{p.name}</div>
                          <div className="text-xs text-stone-500">{p.format}{p.stock !== undefined && p.stock !== null && p.stock <= 0 && <span className="text-red-600 ml-2">Esaurito</span>}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono font-semibold text-[#0F4C81]" data-testid={`listino-price-${p.id}`}>{eur(price(p))}</div>
                          {disc > 0 && <div className="text-[11px] text-stone-400 line-through">{eur(p.agent_price ?? p.public_price)}</div>}
                          {last[p.id] && (
                            <div className={`text-[11px] ${Number(last[p.id].unit_price) < price(p) ? "text-amber-700" : Number(last[p.id].unit_price) > price(p) ? "text-[#15803D]" : "text-stone-400"}`} data-testid={`listino-last-${p.id}`} title={`Ultimo ordine del ${new Date(last[p.id].date).toLocaleDateString("it-IT")}`}>
                              Ultima volta {eur(last[p.id].unit_price)}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => set(p.id, (qty[p.id] || 0) - 1)} className="w-9 h-9 rounded-full border border-stone-200 flex items-center justify-center" data-testid={`listino-minus-${p.id}`}><Minus className="w-4 h-4" /></button>
                          <input type="number" min={0} value={qty[p.id] || 0} onChange={(e) => set(p.id, Number(e.target.value) || 0)} className="w-12 h-9 text-center border border-stone-200 rounded-lg font-mono text-sm" data-testid={`listino-qty-${p.id}`} />
                          <button onClick={() => set(p.id, (qty[p.id] || 0) + 1)} className="w-9 h-9 rounded-full bg-[#0F4C81] text-white flex items-center justify-center" data-testid={`listino-plus-${p.id}`}><Plus className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-stone-200 p-5 lg:sticky lg:top-24" data-testid="listino-summary">
              <h2 className="font-serif text-xl font-semibold text-[#0B2A45] mb-3">Riepilogo ordine</h2>
              {lines.length === 0 && <p className="text-sm text-stone-400">Aggiungi le quantità dal listino.</p>}
              <div className="space-y-1 text-sm">
                {lines.map((l) => <div key={l.p.id} className="flex justify-between"><span className="text-stone-600 truncate">{l.q} × {l.p.name}</span><span className="font-mono">{eur(l.line)}</span></div>)}
              </div>
              {lines.length > 0 && (
                <>
                  <div className="flex justify-between font-semibold border-t pt-3 mt-3"><span>Imponibile</span><span className="font-mono text-[#0F4C81]" data-testid="listino-total">{eur(total)}</span></div>
                  <div className="flex justify-between text-xs text-stone-500"><span>IVA 22%</span><span className="font-mono">{eur(total * 0.22)}</span></div>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note per l'agente (consegna, orari…)" rows={2} className="mt-3" data-testid="listino-notes" />
                  <Button onClick={submit} disabled={busy} className="w-full h-12 rounded-full bg-[#0F4C81] hover:bg-[#0A365C] text-white mt-3" data-testid="listino-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Invia ordine all'agente</>}
                  </Button>
                </>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="listino-orders">
              <h2 className="font-serif text-lg font-semibold text-[#0B2A45] mb-3 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#0F4C81]" />I miei ordini <span className="text-stone-400 text-sm">({orders.length})</span></h2>
              {orders.length > 0 && (
                <Button variant="outline" onClick={repeatLast} className="w-full h-11 rounded-full border-[#0F4C81] text-[#0F4C81] mb-3" data-testid="listino-repeat-btn"><Repeat className="w-4 h-4 mr-2" />Ripeti ultimo ordine</Button>
              )}
              {orders.length === 0 && <p className="text-sm text-stone-400">Nessun ordine inviato.</p>}
              <div className="space-y-2">
                {orders.slice(0, 10).map((o) => (
                  <div key={o.id} className="flex items-center justify-between text-sm border-b border-stone-100 pb-2" data-testid={`listino-order-${o.id}`}>
                    <div><div className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString("it-IT")}</div><div className="text-stone-700 truncate max-w-[180px]">{(o.items || []).map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div></div>
                    <div className="text-right"><div className="font-mono font-semibold text-[#0F4C81]">{eur(o.total)}</div><StatusBadge status={o.status} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
